import { ReactElement, useState } from 'react';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { ChipInput } from '../ChipInput/ChipInput';

// Standard email regex pattern (RFC 5322 compliant, simplified)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type EmailChipInputProps = {
  placeholder?: string;
  /** Array of email values (strings) */
  values: string[];
  /** Callback when emails change - receives array of email strings */
  onChange: (values: string[]) => void;
  /** Callback when an email is removed */
  onRemove?: (value: string) => void;
  width?: string | number;
  minWidth?: string | number;
  /** Maximum height before scrolling (in pixels or chakra units) */
  maxHeight?: string | number;
  /** Whether to allow duplicate emails */
  allowDuplicates?: boolean;
  /** Custom error message for invalid emails */
  invalidEmailMessage?: string;
};

/**
 * EmailChipInput component - A text input that creates chips from valid email addresses
 * Creates chips when user types space, comma, or presses Enter (only for valid emails)
 * Wraps and extends vertically for many entries
 *
 * This is a wrapper around ChipInput that adds email validation logic.
 *
 * @example
 * <EmailChipInput
 *   placeholder="Add email addresses..."
 *   values={emails}
 *   onChange={setEmails}
 * />
 */
export const EmailChipInput = ({
  placeholder = 'Add email addresses...',
  values = [],
  onChange,
  onRemove,
  width = '100%',
  minWidth = '100%',
  maxHeight = '200px',
  allowDuplicates = false,
  invalidEmailMessage = errorMessage.email,
}: EmailChipInputProps): ReactElement => {
  const [hasInvalidEmail, setHasInvalidEmail] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email.trim());
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    // Split by common delimiters: comma, semicolon, space, newline
    const emails = pastedText
      .split(/[,;\s\n]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const validEmails: string[] = [];
    const invalidEmails: string[] = [];

    emails.forEach((email) => {
      if (validateEmail(email)) {
        if (allowDuplicates || !values.includes(email)) {
          validEmails.push(email);
        }
      } else {
        invalidEmails.push(email);
      }
    });

    // Add valid emails as chips immediately
    if (validEmails.length > 0) {
      onChange([...values, ...validEmails]);
    }

    // Handle invalid emails: put them in input field for correction, or clear if all valid
    if (invalidEmails.length > 0) {
      setInputValue(invalidEmails.join(', '));
      setHasInvalidEmail(true);
    } else {
      setInputValue('');
      setHasInvalidEmail(false);
    }
  };

  return (
    <ChipInput
      placeholder={placeholder}
      values={values}
      onChange={onChange}
      onRemove={onRemove}
      width={width}
      minWidth={minWidth}
      maxHeight={maxHeight}
      allowDuplicates={allowDuplicates}
      validate={validateEmail}
      onValidationError={setHasInvalidEmail}
      hasError={hasInvalidEmail}
      errorMessage={invalidEmailMessage}
      inputType="email"
      onPaste={handlePaste}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
    />
  );
};
